/**
 * SPF-A1-FRAMEWORK — Website Chat Widget
 * Seven Pillars Publishing FZE — Version 1.0
 *
 * INSTALLATION: Add this single line before </body> on every page:
 *   <script src="/spf-a1-widget.js"></script>
 *
 * That's it. The widget loads itself automatically.
 */

(function () {
  'use strict';

  // ── Configuration ──────────────────────────────────────────
  const CONFIG = {
    apiEndpoint: 'https://spf-a1-framework.vercel.app/api/chat',
    subscribeEndpoint: 'https://spf-a1-framework.vercel.app/api/subscribe',
    navy: '#1B2F5E',
    navyLight: '#243d73',
    gold: '#B8922A',
    goldLight: '#f5e6c8',
    triggerDelay: 8000,          // Auto-open bubble after 8 seconds
    emailPromptAfter: 3,         // Show email capture after N user messages
    openingMessage: `Welcome. I'm the Seven Pillars educational assistant. I can help you explore the Seven Pillars of Freedom framework — from the foundations of national sovereignty and natural rights to public credit, manufacturing, infrastructure, and innovation.\n\nWhat would you like to understand today?`
  };

  const CHIPS = [
    'What are the Seven Pillars of Freedom?',
    'What is the American System of Economics?',
    'How does Natural Law relate to the Constitution?',
    'What does "Libertatem Reserare" mean?',
    'How can I apply this framework to my country?'
  ];

  // ── State ───────────────────────────────────────────────────
  let conversationHistory = [];
  let userMessageCount = 0;
  let emailCaptured = false;
  let isOpen = false;
  let isLoading = false;

  // ── Inject Styles ───────────────────────────────────────────
  const css = `
    #spf-widget-bubble {
      position: fixed; bottom: 24px; right: 24px; z-index: 9998;
      width: 60px; height: 60px; border-radius: 50%;
      background: ${CONFIG.navy}; cursor: pointer;
      box-shadow: 0 4px 16px rgba(0,0,0,0.3);
      display: flex; align-items: center; justify-content: center;
      transition: transform 0.2s, box-shadow 0.2s;
      border: 2px solid ${CONFIG.gold};
    }
    #spf-widget-bubble:hover { transform: scale(1.07); box-shadow: 0 6px 20px rgba(0,0,0,0.4); }
    #spf-widget-bubble svg { width: 28px; height: 28px; fill: ${CONFIG.gold}; }

    #spf-widget-window {
      position: fixed; bottom: 96px; right: 24px; z-index: 9999;
      width: 380px; max-height: 560px;
      background: #fff; border-radius: 12px;
      box-shadow: 0 8px 32px rgba(0,0,0,0.2);
      display: flex; flex-direction: column; overflow: hidden;
      transform: scale(0.9) translateY(20px); opacity: 0;
      transition: transform 0.25s ease, opacity 0.25s ease;
      pointer-events: none;
      font-family: Georgia, 'Times New Roman', serif;
    }
    #spf-widget-window.spf-open {
      transform: scale(1) translateY(0); opacity: 1; pointer-events: all;
    }
    @media (max-width: 440px) {
      #spf-widget-window { width: calc(100vw - 16px); right: 8px; bottom: 88px; }
    }

    #spf-widget-header {
      background: ${CONFIG.navy}; color: white;
      padding: 14px 16px; display: flex; align-items: center;
      justify-content: space-between; flex-shrink: 0;
    }
    #spf-widget-header-title { display: flex; align-items: center; gap: 10px; }
    #spf-widget-header-title svg { width: 22px; height: 22px; fill: ${CONFIG.gold}; flex-shrink: 0; }
    #spf-widget-header-name { font-size: 15px; font-weight: bold; color: white; letter-spacing: 0.3px; }
    #spf-widget-header-sub { font-size: 11px; color: ${CONFIG.gold}; font-style: italic; margin-top: 1px; }
    #spf-widget-close {
      background: none; border: none; cursor: pointer;
      color: rgba(255,255,255,0.7); font-size: 20px; padding: 0 4px;
      line-height: 1; transition: color 0.15s;
    }
    #spf-widget-close:hover { color: white; }

    #spf-widget-messages {
      flex: 1; overflow-y: auto; padding: 16px; display: flex;
      flex-direction: column; gap: 12px; min-height: 200px; max-height: 320px;
    }
    #spf-widget-messages::-webkit-scrollbar { width: 4px; }
    #spf-widget-messages::-webkit-scrollbar-track { background: #f5f5f5; }
    #spf-widget-messages::-webkit-scrollbar-thumb { background: ${CONFIG.gold}; border-radius: 2px; }

    .spf-msg {
      max-width: 85%; padding: 10px 13px; border-radius: 10px;
      font-size: 14px; line-height: 1.55; word-wrap: break-word;
    }
    .spf-msg-agent {
      background: #f0f4fb; color: #1a1a1a; align-self: flex-start;
      border-bottom-left-radius: 3px;
    }
    .spf-msg-user {
      background: ${CONFIG.navy}; color: white; align-self: flex-end;
      border-bottom-right-radius: 3px;
    }
    .spf-typing {
      background: #f0f4fb; align-self: flex-start;
      padding: 12px 16px; border-radius: 10px; border-bottom-left-radius: 3px;
      display: flex; gap: 5px; align-items: center;
    }
    .spf-dot {
      width: 7px; height: 7px; border-radius: 50%;
      background: ${CONFIG.navy}; opacity: 0.5;
      animation: spf-bounce 1.2s infinite;
    }
    .spf-dot:nth-child(2) { animation-delay: 0.2s; }
    .spf-dot:nth-child(3) { animation-delay: 0.4s; }
    @keyframes spf-bounce {
      0%, 60%, 100% { transform: translateY(0); }
      30% { transform: translateY(-6px); }
    }

    #spf-widget-chips {
      padding: 0 16px 12px; display: flex; flex-wrap: wrap; gap: 7px;
    }
    .spf-chip {
      background: ${CONFIG.goldLight}; color: ${CONFIG.navy};
      border: 1px solid ${CONFIG.gold}; border-radius: 16px;
      padding: 5px 11px; font-size: 12px; cursor: pointer;
      font-family: Georgia, serif; transition: background 0.15s;
      line-height: 1.4;
    }
    .spf-chip:hover { background: ${CONFIG.gold}; color: white; }

    #spf-widget-email-capture {
      margin: 0 16px 12px; background: ${CONFIG.goldLight};
      border: 1px solid ${CONFIG.gold}; border-radius: 8px;
      padding: 12px; display: none;
    }
    #spf-widget-email-capture.spf-show { display: block; }
    #spf-email-prompt {
      font-size: 13px; color: ${CONFIG.navy}; margin-bottom: 8px;
      line-height: 1.4;
    }
    #spf-email-form { display: flex; gap: 6px; }
    #spf-email-input {
      flex: 1; padding: 7px 10px; border: 1px solid ${CONFIG.gold};
      border-radius: 6px; font-size: 13px; font-family: Georgia, serif;
      outline: none; color: #1a1a1a;
    }
    #spf-email-input:focus { border-color: ${CONFIG.navy}; }
    #spf-email-submit {
      background: ${CONFIG.navy}; color: white; border: none;
      border-radius: 6px; padding: 7px 12px; font-size: 13px;
      cursor: pointer; font-family: Georgia, serif; transition: background 0.15s;
      white-space: nowrap;
    }
    #spf-email-submit:hover { background: ${CONFIG.navyLight}; }
    #spf-email-success { font-size: 13px; color: ${CONFIG.navy}; font-style: italic; }

    #spf-widget-input-area {
      padding: 12px 16px; border-top: 1px solid #eee;
      display: flex; gap: 8px; align-items: flex-end; flex-shrink: 0;
      background: #fafafa;
    }
    #spf-widget-input {
      flex: 1; border: 1px solid #ddd; border-radius: 8px;
      padding: 9px 12px; font-size: 14px; font-family: Georgia, serif;
      resize: none; outline: none; max-height: 100px; line-height: 1.4;
      color: #1a1a1a; background: white; transition: border-color 0.15s;
    }
    #spf-widget-input:focus { border-color: ${CONFIG.navy}; }
    #spf-widget-input::placeholder { color: #aaa; }
    #spf-widget-send {
      background: ${CONFIG.navy}; border: none; border-radius: 8px;
      width: 38px; height: 38px; cursor: pointer; flex-shrink: 0;
      display: flex; align-items: center; justify-content: center;
      transition: background 0.15s;
    }
    #spf-widget-send:hover { background: ${CONFIG.navyLight}; }
    #spf-widget-send:disabled { background: #ccc; cursor: not-allowed; }
    #spf-widget-send svg { width: 18px; height: 18px; fill: white; }
    #spf-widget-footer {
      text-align: center; font-size: 10px; color: #bbb;
      padding: 4px 0 8px; font-family: Georgia, serif;
    }
    #spf-widget-footer a { color: #bbb; text-decoration: none; }
    #spf-widget-footer a:hover { color: ${CONFIG.navy}; }
  `;

  // ── SVG Icons ───────────────────────────────────────────────
  const TEMPLE_SVG = `<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <rect x="2" y="18" width="20" height="2" rx="1"/>
    <rect x="1" y="6" width="22" height="2" rx="1"/>
    <rect x="4" y="8" width="2" height="10"/>
    <rect x="7.5" y="8" width="2" height="10"/>
    <rect x="11" y="8" width="2" height="10"/>
    <rect x="14.5" y="8" width="2" height="10"/>
    <rect x="18" y="8" width="2" height="10"/>
    <polygon points="12,1 2,6 22,6"/>
  </svg>`;

  const SEND_SVG = `<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M2 21l21-9L2 3v7l15 2-15 2z"/>
  </svg>`;

  // ── Build DOM ───────────────────────────────────────────────
  function buildWidget() {
    // Inject CSS
    const style = document.createElement('style');
    style.textContent = css;
    document.head.appendChild(style);

    // Bubble
    const bubble = document.createElement('div');
    bubble.id = 'spf-widget-bubble';
    bubble.setAttribute('aria-label', 'Open Seven Pillars chat');
    bubble.setAttribute('role', 'button');
    bubble.innerHTML = TEMPLE_SVG;
    bubble.onclick = toggleWidget;

    // Window
    const win = document.createElement('div');
    win.id = 'spf-widget-window';
    win.setAttribute('aria-live', 'polite');
    win.innerHTML = `
      <div id="spf-widget-header">
        <div id="spf-widget-header-title">
          ${TEMPLE_SVG}
          <div>
            <div id="spf-widget-header-name">Seven Pillars</div>
            <div id="spf-widget-header-sub">Libertatem Reserare</div>
          </div>
        </div>
        <button id="spf-widget-close" aria-label="Close chat">✕</button>
      </div>
      <div id="spf-widget-messages"></div>
      <div id="spf-widget-chips"></div>
      <div id="spf-widget-email-capture">
        <div id="spf-email-prompt">Enjoying the conversation? Subscribe to the Seven Pillars newsletter for deeper insights.</div>
        <div id="spf-email-form">
          <input type="email" id="spf-email-input" placeholder="Your email address" autocomplete="email"/>
          <button id="spf-email-submit" type="button">Subscribe</button>
        </div>
        <div id="spf-email-success" style="display:none;">✓ Thank you — you're subscribed.</div>
      </div>
      <div id="spf-widget-input-area">
        <textarea id="spf-widget-input" placeholder="Ask about any Pillar..." rows="1" aria-label="Message input"></textarea>
        <button id="spf-widget-send" aria-label="Send message">${SEND_SVG}</button>
      </div>
      <div id="spf-widget-footer">Powered by <a href="https://sevenpillars.com" target="_blank">Seven Pillars</a></div>
    `;

    document.body.appendChild(bubble);
    document.body.appendChild(win);

    // Event listeners
    document.getElementById('spf-widget-close').onclick = toggleWidget;
    document.getElementById('spf-widget-send').onclick = handleSend;
    document.getElementById('spf-widget-input').addEventListener('keydown', function (e) {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    });
    document.getElementById('spf-widget-input').addEventListener('input', autoResize);
    document.getElementById('spf-email-submit').onclick = handleEmailSubmit;

    renderChips();
    displayOpeningMessage();
  }

  function autoResize() {
    const el = document.getElementById('spf-widget-input');
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 100) + 'px';
  }

  // ── Opening Message ─────────────────────────────────────────
  function displayOpeningMessage() {
    addMessageToUI('agent', CONFIG.openingMessage);
  }

  // ── Suggestion Chips ────────────────────────────────────────
  function renderChips() {
    const container = document.getElementById('spf-widget-chips');
    container.innerHTML = '';
    CHIPS.forEach(function (text) {
      const chip = document.createElement('button');
      chip.className = 'spf-chip';
      chip.textContent = text;
      chip.onclick = function () {
        container.innerHTML = '';
        sendMessage(text);
      };
      container.appendChild(chip);
    });
  }

  // ── Toggle Window ───────────────────────────────────────────
  function toggleWidget() {
    isOpen = !isOpen;
    const win = document.getElementById('spf-widget-window');
    if (isOpen) {
      win.classList.add('spf-open');
      setTimeout(function () {
        document.getElementById('spf-widget-input').focus();
      }, 280);
    } else {
      win.classList.remove('spf-open');
    }
  }

  // ── Send Message ────────────────────────────────────────────
  function handleSend() {
    const input = document.getElementById('spf-widget-input');
    const text = input.value.trim();
    if (!text || isLoading) return;
    input.value = '';
    input.style.height = 'auto';
    // Hide chips after first message
    document.getElementById('spf-widget-chips').innerHTML = '';
    sendMessage(text);
  }

  async function sendMessage(text) {
    if (isLoading) return;
    isLoading = true;
    userMessageCount++;

    addMessageToUI('user', text);
    conversationHistory.push({ role: 'user', content: text });

    // Typing indicator
    const typingId = showTyping();
    setSendDisabled(true);

    try {
      const response = await fetch(CONFIG.apiEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages: conversationHistory })
      });

      const data = await response.json();
      removeTyping(typingId);

      const reply = data.reply || 'I apologise — something went wrong. Please try again.';
      addMessageToUI('agent', reply);
      conversationHistory.push({ role: 'assistant', content: reply });

      // Trim history client-side too
      if (conversationHistory.length > 20) {
        conversationHistory = conversationHistory.slice(-20);
      }

      // Show email capture after N exchanges
      if (userMessageCount >= CONFIG.emailPromptAfter && !emailCaptured) {
        document.getElementById('spf-widget-email-capture').classList.add('spf-show');
      }

    } catch (error) {
      removeTyping(typingId);
      addMessageToUI('agent', 'I apologise — I encountered a technical issue. Please try again.');
      console.error('SPF-A1 widget error:', error);
    }

    isLoading = false;
    setSendDisabled(false);
  }

  // ── Email Capture ───────────────────────────────────────────
  async function handleEmailSubmit() {
    const input = document.getElementById('spf-email-input');
    const email = input.value.trim();
    if (!email || !email.includes('@')) {
      input.focus();
      return;
    }

    const btn = document.getElementById('spf-email-submit');
    btn.disabled = true;
    btn.textContent = '...';

    try {
      const response = await fetch(CONFIG.subscribeEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      const data = await response.json();

      if (data.success) {
        document.getElementById('spf-email-form').style.display = 'none';
        document.getElementById('spf-email-success').style.display = 'block';
        emailCaptured = true;
      } else {
        btn.disabled = false;
        btn.textContent = 'Subscribe';
      }
    } catch (e) {
      btn.disabled = false;
      btn.textContent = 'Subscribe';
    }
  }

  // ── UI Helpers ──────────────────────────────────────────────
  function addMessageToUI(role, text) {
    const messages = document.getElementById('spf-widget-messages');
    const msg = document.createElement('div');
    msg.className = 'spf-msg ' + (role === 'agent' ? 'spf-msg-agent' : 'spf-msg-user');
    // Render Markdown for agent messages; plain text for user messages
    msg.innerHTML = role === 'agent' ? renderMarkdown(text) : escapeHtml(text).replace(/\n/g, '<br>');
    messages.appendChild(msg);
    messages.scrollTop = messages.scrollHeight;
  }

  function showTyping() {
    const messages = document.getElementById('spf-widget-messages');
    const id = 'spf-typing-' + Date.now();
    const el = document.createElement('div');
    el.className = 'spf-typing';
    el.id = id;
    el.innerHTML = '<div class="spf-dot"></div><div class="spf-dot"></div><div class="spf-dot"></div>';
    messages.appendChild(el);
    messages.scrollTop = messages.scrollHeight;
    return id;
  }

  function removeTyping(id) {
    const el = document.getElementById(id);
    if (el) el.remove();
  }

  function setSendDisabled(disabled) {
    document.getElementById('spf-widget-send').disabled = disabled;
  }

  function escapeHtml(text) {
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function renderMarkdown(text) {
    return escapeHtml(text)
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.+?)\*/g, '<em>$1</em>')
      .replace(/\n/g, '<br>');
  }

  // ── Init ────────────────────────────────────────────────────
  function init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', buildWidget);
    } else {
      buildWidget();
    }
  }

  init();

})();
